import { useMemo, useState, useEffect } from 'react';
import { useBoolean, useThrottleFn } from 'ahooks';
import { MAX_ROW_NUM } from '../constants';

import type { ICalendarColCfgMapping } from '../types';

export default function useFoldable(
  // 是否折叠的初始值
  initialState?: boolean,
  props?: {
    // 是否正在加载日历数据
    isLoading?: boolean;
    // 是否无日历数据
    isNoData?: boolean;
    // 日历数据
    calendarDataList?: CommonCalendarDataTypes;
    // 列信息映射（键为列对应的时间，值为列的信息）
    colCfgMapping?: ICalendarColCfgMapping;
    // 获取页面容器（支持纵向滚动那个dom）
    getPageContainerEl?: () => HTMLElement | null;
  },
) {
  const { isLoading = true, isNoData = true, calendarDataList, colCfgMapping, getPageContainerEl } = props || {};

  // 是否折叠，以及切换方法
  const [fold, { toggle: toggleFold }] = useBoolean(!!initialState);

  // 是否滚动页面到达底部
  const [isScroll2PageBottom, setIsScroll2PageBottom] = useState(false);

  // 是否存在大于MAX_ROW_NUM配置的行数的日历卡片
  const isOverMaxRows = useMemo<boolean>(() => {
    if (isLoading || isNoData) return false;
    if (!colCfgMapping || !Array.isArray(calendarDataList) || !calendarDataList.length) return false;
    return calendarDataList.some((calendarDataItem) => {
      const { date, list } = calendarDataItem || {};
      if (!date || !Array.isArray(list) || !list.length) return false;
      const colCfg = colCfgMapping[date];
      const cardsNumPerRow = colCfg?.cardsNumPerRow || 0;
      return list.length > cardsNumPerRow * MAX_ROW_NUM;
    });
  }, [isLoading, isNoData, calendarDataList, colCfgMapping]);

  // 是否展示折叠/展开按钮
  const isShowAllBtn = useMemo<boolean>(() => false, []);

  // 是否展示折叠/展开按钮
  // const isShowAllBtn = useMemo<boolean>(() => isOverMaxRows, [isOverMaxRows]);

  const { run: calcIsScroll2PageBottomRun } = useThrottleFn(
    (params: { scrollTop: number; scrollHeight: number; offsetHeight: number }) => {
      const { scrollTop, scrollHeight, offsetHeight } = params || {};
      const maxScrollTop = scrollHeight - offsetHeight;
      const flag = scrollTop >= maxScrollTop;
      setIsScroll2PageBottom(flag);
    },
    { wait: 50, leading: true, trailing: true },
  );

  const handlePageScroll = (e: any) => {
    const { scrollTop = 0, scrollHeight = 0, offsetHeight = 0 } = e?.target || {};
    calcIsScroll2PageBottomRun({
      scrollTop,
      scrollHeight,
      offsetHeight,
    });
  };

  useEffect(() => {
    const pageContainer = getPageContainerEl?.();
    if (!pageContainer) return;

    pageContainer.addEventListener('scroll', handlePageScroll);

    return () => {
      pageContainer.removeEventListener('scroll', handlePageScroll);
    };
  }, []);

  return {
    // 是否滚动页面到达底部
    isScroll2PageBottom,
    // 是否存在大于4行的日历卡片
    isOverMaxRows,
    // 是否展示折叠/展开按钮
    isShowAllBtn,
    // 是否折叠
    fold,
    // 切换是否折叠
    toggleFold,
  };
}
